/**
 * AI Tutor V2 Redux Slice
 * Manages state for AI Tutor packages, sessions, and learning
 *
 * New approach:
 * - ZIP packages instead of PDFs
 * - HTML chapter rendering in iframe
 * - RAG-powered Q&A
 * - Voice doubts
 * - Doubt quota tracking
 */

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as aiTutorApi from "./aiTutorApi";

// An error as text. The banner renders this directly, and a response *object*
// there throws inside React and takes the whole reader down.
const errorText = (error) => {
  const data = error?.response?.data;
  if (typeof data === "string") return data;
  if (data?.error || data?.detail) return data.error || data.detail;
  // DRF field errors: {"message": ["This field may not be blank."]}
  if (data && typeof data === "object") {
    const first = Object.values(data).flat().find((v) => typeof v === "string");
    if (first) return first;
  }
  return error?.message || "Something went wrong. Please try again.";
};


// ─── Async Thunks ────────────────────────────────────────────────────────────

// Package thunks
export const fetchPackages = createAsyncThunk(
  "aiTutor/fetchPackages",
  async (topicId, { rejectWithValue }) => {
    try {
      const data = await aiTutorApi.getPackages(topicId);
      return data;
    } catch (error) {
      return rejectWithValue(errorText(error));
    }
  }
);

export const uploadPackage = createAsyncThunk(
  "aiTutor/uploadPackage",
  async ({ topicId, title, file }, { rejectWithValue }) => {
    try {
      // Step 1: Get presigned URL
      const { upload_url, s3_key, package_id } = await aiTutorApi.getUploadUrl(
        topicId,
        title,
        file.name
      );

      // Step 2: Upload to S3
      await aiTutorApi.uploadToS3(upload_url, file);

      // Step 3: Trigger processing
      await aiTutorApi.processPackage(package_id, s3_key);

      return { package_id, message: "Package uploaded and processing started" };
    } catch (error) {
      return rejectWithValue(errorText(error));
    }
  }
);

// Session thunks
export const fetchSessions = createAsyncThunk(
  "aiTutor/fetchSessions",
  async (_, { rejectWithValue }) => {
    try {
      const data = await aiTutorApi.getSessions();
      return data;
    } catch (error) {
      return rejectWithValue(errorText(error));
    }
  }
);

export const fetchSessionDetail = createAsyncThunk(
  "aiTutor/fetchSessionDetail",
  async (sessionId, { rejectWithValue }) => {
    try {
      const data = await aiTutorApi.getSessionDetail(sessionId);
      return data;
    } catch (error) {
      return rejectWithValue(errorText(error));
    }
  }
);

export const fetchEarlierChat = createAsyncThunk(
  "aiTutor/fetchEarlierChat",
  async ({ sessionId, before }, { rejectWithValue }) => {
    try {
      return await aiTutorApi.getEarlierChat(sessionId, before);
    } catch (error) {
      return rejectWithValue(errorText(error));
    }
  }
);

export const createSession = createAsyncThunk(
  "aiTutor/createSession",
  async (packageId, { rejectWithValue }) => {
    try {
      const data = await aiTutorApi.createSession(packageId);
      return data;
    } catch (error) {
      return rejectWithValue(errorText(error));
    }
  }
);

export const visitChapter = createAsyncThunk(
  "aiTutor/visitChapter",
  async ({ sessionId, chapterIndex }, { rejectWithValue }) => {
    try {
      const data = await aiTutorApi.visitChapter(sessionId, chapterIndex);
      return { ...data, chapterIndex };
    } catch (error) {
      return rejectWithValue(errorText(error));
    }
  }
);

// Chat thunks
export const sendChatMessage = createAsyncThunk(
  "aiTutor/sendChatMessage",
  async (
    { sessionId, message, chapterIndex, contextText, imageData, section, screen },
    { rejectWithValue, dispatch, signal }
  ) => {
    try {
      const data = await aiTutorApi.sendChatMessage(
        sessionId,
        message,
        chapterIndex,
        contextText,
        imageData,
        section || "",
        (progress) => dispatch({ type: "aiTutor/setChatProgress", payload: progress }),
        signal,
        screen || {}
      );
      return { ...data, userMessage: message };
    } catch (error) {
      return rejectWithValue(errorText(error));
    }
  }
);

export const sendVoiceDoubt = createAsyncThunk(
  "aiTutor/sendVoiceDoubt",
  async (
    { sessionId, audioFile, chapterIndex, contextText, section },
    { rejectWithValue, dispatch, signal }
  ) => {
    try {
      const data = await aiTutorApi.sendVoiceDoubt(
        sessionId,
        audioFile,
        chapterIndex,
        contextText,
        section || "",
        (progress) => dispatch({ type: "aiTutor/setChatProgress", payload: progress }),
        signal
      );
      return data;
    } catch (error) {
      return rejectWithValue(errorText(error));
    }
  }
);

// ─── Initial State ───────────────────────────────────────────────────────────

const initialState = {
  // Packages (replacing documents)
  packages: [],
  packagesLoading: false,
  packagesError: null,

  // Sessions
  sessions: [],
  sessionsLoading: false,
  sessionsError: null,

  // Current session (learning state)
  currentSession: null,
  sessionLoading: false,
  sessionError: null,

  // Chapter state
  currentChapterIndex: 0,
  visitedChapters: [],

  // Chat messages: the latest page, and older pages as they are asked for.
  chatMessages: [],
  chatHasMore: false,
  chatOldestTurn: null,
  chatLoadingEarlier: false,
  chatLoading: false,
  chatError: null,
  // {state: 'queued'|'running', position} while a question waits for the tutor.
  chatProgress: null,
  // The optimistic message of the question still being answered, if any.
  pendingMessageId: null,

  // Doubt quota
  doubtTurns: 0,
  doubtLimit: 50,
  doubtsRemaining: 50,

  // Upload state
  uploadLoading: false,
  uploadError: null,
  uploadProgress: 0,

  // Voice recording state
  isRecording: false,
  voiceLoading: false,
  voiceError: null,
};

// ─── Slice ───────────────────────────────────────────────────────────────────

const aiTutorSlice = createSlice({
  name: "aiTutor",
  initialState,
  reducers: {
    setChatProgress: (state, action) => {
      state.chatProgress = action.payload;
    },
    clearSessionState: (state) => {
      state.currentSession = null;
      state.sessionError = null;
      // A question still waiting belongs to the lesson being left.
      state.chatLoading = false;
      state.voiceLoading = false;
      state.chatProgress = null;
      state.chatError = null;
      state.voiceError = null;
      state.pendingMessageId = null;
      state.currentChapterIndex = 0;
      state.visitedChapters = [];
      state.chatMessages = [];
      state.chatHasMore = false;
      state.chatOldestTurn = null;
      state.chatLoadingEarlier = false;
      state.doubtTurns = 0;
      state.doubtLimit = 50;
      state.doubtsRemaining = 50;
    },
    clearErrors: (state) => {
      state.packagesError = null;
      state.sessionsError = null;
      state.sessionError = null;
      state.chatError = null;
      state.uploadError = null;
      state.voiceError = null;
    },
    setCurrentChapterIndex: (state, action) => {
      state.currentChapterIndex = action.payload;
    },
    setIsRecording: (state, action) => {
      state.isRecording = action.payload;
    },
    addOptimisticMessage: (state, action) => {
      // Add user message optimistically before API response. Its id is
      // remembered: it is the one question still waiting for an answer.
      const id = `temp-${Date.now()}`;
      state.pendingMessageId = id;
      state.chatMessages.push({
        id,
        role: "user",
        content: action.payload.message,
        chapter_index: action.payload.chapterIndex,
        timestamp: new Date().toISOString(),
      });
    },
  },
  extraReducers: (builder) => {
    // ─── Fetch Packages ──────────────────────────────────────────────────────
    builder
      .addCase(fetchPackages.pending, (state) => {
        state.packagesLoading = true;
        state.packagesError = null;
      })
      .addCase(fetchPackages.fulfilled, (state, action) => {
        state.packagesLoading = false;
        state.packages = action.payload;
      })
      .addCase(fetchPackages.rejected, (state, action) => {
        state.packagesLoading = false;
        state.packagesError = action.payload;
      });

    // ─── Upload Package ──────────────────────────────────────────────────────
    builder
      .addCase(uploadPackage.pending, (state) => {
        state.uploadLoading = true;
        state.uploadError = null;
        state.uploadProgress = 0;
      })
      .addCase(uploadPackage.fulfilled, (state) => {
        state.uploadLoading = false;
        state.uploadProgress = 100;
      })
      .addCase(uploadPackage.rejected, (state, action) => {
        state.uploadLoading = false;
        state.uploadError = action.payload;
      });

    // ─── Fetch Sessions ──────────────────────────────────────────────────────
    builder
      .addCase(fetchSessions.pending, (state) => {
        state.sessionsLoading = true;
        state.sessionsError = null;
      })
      .addCase(fetchSessions.fulfilled, (state, action) => {
        state.sessionsLoading = false;
        state.sessions = action.payload;
      })
      .addCase(fetchSessions.rejected, (state, action) => {
        state.sessionsLoading = false;
        state.sessionsError = action.payload;
      });

    // ─── Fetch Session Detail ────────────────────────────────────────────────
    builder
      .addCase(fetchSessionDetail.pending, (state) => {
        state.sessionLoading = true;
        state.sessionError = null;
      })
      .addCase(fetchSessionDetail.fulfilled, (state, action) => {
        state.sessionLoading = false;
        // A refresh of the session already open keeps the chapter the student
        // is on; the server's value is only where they first got to.
        const sameSession = state.currentSession?.session_id === action.payload.session_id;
        state.currentSession = action.payload;
        if (!sameSession) state.currentChapterIndex = action.payload.current_chapter_index || 0;
        state.visitedChapters = action.payload.visited_chapters || [];
        // Keep the typed question still waiting for its answer — only that
        // one: a refresh while it is in flight (after a voice doubt, say) must
        // not drop it, and must not duplicate questions already answered.
        const pending = state.chatLoading && state.pendingMessageId
          ? state.chatMessages.filter((m) => m.id === state.pendingMessageId)
          : [];
        // A refresh of the open session (after a voice doubt, say) brings the
        // latest page only; pages the student already loaded above it stay.
        const fresh = action.payload.chat_messages || [];
        const newOldest = action.payload.chat_oldest_turn ?? null;
        const keepOlder = sameSession && newOldest !== null && action.payload.chat_has_more
          && state.chatOldestTurn !== null && state.chatOldestTurn < newOldest;
        const older = keepOlder
          ? state.chatMessages.filter((m) => (typeof m.i === "number" && m.i < newOldest)
              || (m.i === null && String(m.id).startsWith("h-old-")))
          : [];
        state.chatMessages = [...older, ...fresh, ...pending];
        if (keepOlder) {
          // The cursor and has_more stay where the student had paged back to.
        } else {
          state.chatHasMore = Boolean(action.payload.chat_has_more);
          state.chatOldestTurn = newOldest;
        }
        state.doubtTurns = action.payload.doubt_turns || 0;
        state.doubtLimit = action.payload.doubt_limit || 50;
        state.doubtsRemaining = action.payload.doubts_remaining || 50;
      })
      .addCase(fetchSessionDetail.rejected, (state, action) => {
        state.sessionLoading = false;
        state.sessionError = action.payload;
      });

    // ─── Earlier conversation ────────────────────────────────────────────────
    builder
      .addCase(fetchEarlierChat.pending, (state) => {
        state.chatLoadingEarlier = true;
      })
      .addCase(fetchEarlierChat.fulfilled, (state, action) => {
        state.chatLoadingEarlier = false;
        // The student may have left for another lesson while this was loading.
        if (action.meta.arg.sessionId !== state.currentSession?.session_id) return;
        const have = new Set(state.chatMessages.map((m) => m.id));
        const older = action.payload.messages.filter((m) => !have.has(m.id));
        state.chatMessages = [...older, ...state.chatMessages];
        state.chatHasMore = action.payload.hasMore;
        if (action.payload.oldestTurn !== null) state.chatOldestTurn = action.payload.oldestTurn;
      })
      .addCase(fetchEarlierChat.rejected, (state) => {
        state.chatLoadingEarlier = false;
      });

    // ─── Create Session ──────────────────────────────────────────────────────
    builder
      .addCase(createSession.pending, (state) => {
        state.sessionLoading = true;
        state.sessionError = null;
      })
      .addCase(createSession.fulfilled, (state, action) => {
        state.sessionLoading = false;
        state.currentSession = action.payload;
      })
      .addCase(createSession.rejected, (state, action) => {
        state.sessionLoading = false;
        state.sessionError = action.payload;
      });

    // ─── Visit Chapter ───────────────────────────────────────────────────────
    builder
      .addCase(visitChapter.fulfilled, (state, action) => {
        state.currentChapterIndex = action.payload.chapterIndex;
        state.visitedChapters = action.payload.visited_chapters;
        if (state.currentSession) {
          state.currentSession.progress_percentage =
            action.payload.progress_percentage;
        }
      });

    // ─── Send Chat Message ───────────────────────────────────────────────────
    builder
      .addCase(sendChatMessage.pending, (state) => {
        state.chatLoading = true;
        state.chatError = null;
      })
      .addCase(sendChatMessage.fulfilled, (state, action) => {
        // An answer for a lesson that is no longer open is dropped.
        if (action.meta.arg.sessionId !== state.currentSession?.session_id) return;
        state.chatLoading = false;
        state.chatProgress = null;
        // The question is answered: no longer pending.
        state.chatMessages.forEach((m) => {
          if (m.id === state.pendingMessageId) m.id = `u-${m.id.slice(5)}`;
        });
        state.pendingMessageId = null;
        // Add assistant response
        state.chatMessages.push({
          id: `msg-${Date.now()}`,
          role: "assistant",
          content: action.payload.response,
          rag_sources: action.payload.sources || [],
          timestamp: new Date().toISOString(),
        });
        // Update doubt quota
        state.doubtTurns = action.payload.doubt_turns;
        state.doubtLimit = action.payload.doubt_limit;
        state.doubtsRemaining = action.payload.doubts_remaining;
      })
      .addCase(sendChatMessage.rejected, (state, action) => {
        if (action.meta.aborted || action.meta.arg.sessionId !== state.currentSession?.session_id) return;
        state.chatLoading = false;
        state.chatProgress = null;
        const failedId = state.pendingMessageId;
        state.pendingMessageId = null;
        state.chatError = action.payload;
        // Remove the failed question's optimistic message — only that one.
        state.chatMessages = state.chatMessages.filter((msg) => msg.id !== failedId);
      });

    // ─── Send Voice Doubt ────────────────────────────────────────────────────
    builder
      .addCase(sendVoiceDoubt.pending, (state) => {
        state.voiceLoading = true;
        state.voiceError = null;
        state.isRecording = false;
      })
      .addCase(sendVoiceDoubt.fulfilled, (state, action) => {
        state.voiceLoading = false;
        state.chatProgress = null;
        // Add transcribed user message
        state.chatMessages.push({
          id: `voice-user-${Date.now()}`,
          role: "user",
          content: action.payload.transcript,
          is_voice: true,
          timestamp: new Date().toISOString(),
        });
        // Add assistant response
        state.chatMessages.push({
          id: `voice-assistant-${Date.now()}`,
          role: "assistant",
          content: action.payload.response,
          rag_sources: action.payload.sources || [],
          timestamp: new Date().toISOString(),
        });
        // Update doubt quota
        state.doubtTurns = action.payload.doubt_turns;
        state.doubtLimit = action.payload.doubt_limit;
        state.doubtsRemaining = action.payload.doubts_remaining;
      })
      .addCase(sendVoiceDoubt.rejected, (state, action) => {
        state.voiceLoading = false;
        state.chatProgress = null;
        state.voiceError = action.payload;
      });
  },
});

export const {
  clearSessionState,
  clearErrors,
  setCurrentChapterIndex,
  setIsRecording,
  addOptimisticMessage,
} = aiTutorSlice.actions;

export default aiTutorSlice.reducer;
